import { getShortAddressFromAddress } from "shared/addressUtils";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { Property } from "types/landlord/address";
import { Dispatch, FC, SetStateAction, useMemo } from "react";

import { getErrorMessage } from "utils/error";
import { getSourceLabel } from "utils/getSourceLabel";
import {
  Action,
  ContextMenuPopover
} from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { Door } from "providers/Layout/components/Door";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { PartnerFeatures, PropertyKind } from "shared/types";

import { Column } from "react-data-grid";
import { DataGridTable } from "shared/components/DataGridTable";
import { handleCopy } from "utils/handleCopy";
import { useLayout } from "providers/Layout/hooks";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { Search01Icon } from "@hugeicons-pro/core-stroke-rounded";

type Props = {
  data: Property[];
  setSelectedPropertyId: Dispatch<SetStateAction<string | null>>;
  setShowDeletePropertyModal: (value: boolean) => void;
  hasSearchParams?: boolean;
  isLoading?: boolean;
  showPropertyGroup?: boolean;
  pagination?: React.ReactNode;
  perPageRows?: number;
  viewPortHeightOffset?: number;
};

export const PropertiesTable: FC<Props> = ({
  data,
  setShowDeletePropertyModal,
  setSelectedPropertyId,
  showPropertyGroup = true,
  isLoading = false,
  pagination,
  perPageRows,
  viewPortHeightOffset,
  hasSearchParams
}) => {
  const { partnerFeatures, type } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );

  const { sectionHeight } = useLayout();

  const {
    Unit,
    Units,
    units,
    Property,
    Properties,
    property: propertyDictionary
  } = useUnitsPropertiesDictionary();

  const navigate = useNavigate();

  const columns = useMemo(() => {
    const isBoomscreenAndPms = type.isBoomScreen && type.isPMS;

    const ret: Column<Property>[] = [
      {
        key: "actions",
        name: "",
        width: 60,
        minWidth: 60,
        frozen: true,
        resizable: false,
        renderCell: ({ row }) => {
          const property = row;

          const popoverActions: Action[] = [
            {
              title: "Review",
              onClick: () => navigate(`/properties/${property.id}`)
            }
          ];

          if (type.isBoomScreen)
            if (type.isBoomCRM)
              popoverActions.push({
                title: `Copy ${propertyDictionary} listing page`,
                onClick: async (e) => {
                  e.stopPropagation();

                  handleCopy(property.listing_url || "", "Link copied");
                }
              });
            else
              popoverActions.push({
                title: `Copy link to ${propertyDictionary}`,
                onClick: async (e) => {
                  e.stopPropagation();

                  try {
                    await navigator.clipboard.writeText(
                      property.magic_link?.application_link || ""
                    );

                    toast.info("Link copied");
                  } catch (err) {
                    toast.error(getErrorMessage(err));
                  }
                },
                disabled: !property.total_units,
                tooltip: `You can't copy a link to a ${propertyDictionary} without ${units}`
              });

          popoverActions.push({
            title: `Delete ${propertyDictionary}`,
            color:
              property.can_delete && !isBoomscreenAndPms
                ? "text-danger-500"
                : "text-content-100",
            onClick: (e) => {
              e.stopPropagation();
              if (property.can_delete && !isBoomscreenAndPms) {
                setShowDeletePropertyModal(true);
                setSelectedPropertyId(String(property.id));
              }
            },
            disabled: !property.can_delete || isBoomscreenAndPms,
            tooltip:
              type.isPMS && type.isBoomScreen
                ? `You can't delete a ${propertyDictionary}`
                : `You can't delete a ${propertyDictionary} with applications`
          });

          return (
            <ContextMenuPopover
              placement="bottom-start"
              triggerClassName="-ml-1.5"
              actions={popoverActions}
            />
          );
        }
      },
      {
        key: "address",
        name: Property,
        minWidth: 300,
        resizable: true,
        renderCell: ({ row }) => (
          <>
            <p className="text-sm font-medium text-content-1000 truncate">
              {row.name}
            </p>
            <p className="text-xs text-content-400 truncate">
              {getShortAddressFromAddress(row.address)}
            </p>
          </>
        )
      },
      {
        key: "total_units",
        name: Units,
        width: 124,
        minWidth: 100,
        resizable: true,
        renderCell: ({ row }) => {
          const title = row.total_units > 1 ? Units : Unit;

          if (row.kind === PropertyKind.SingleFamily)
            return <div>Single family</div>;

          return (
            <div className="flex gap-2">
              <Door className="shrink-0 stroke-content-500" size="20" />
              <p className="text-content-500">
                {row.total_units + " " + title}
              </p>
            </div>
          );
        }
      }
    ];

    if (type.isPMS)
      ret.push({
        key: "pms_id",
        name: "Source",
        width: 135,
        resizable: true,
        renderCell: ({ row }) => (
          <p className="font-medium text-content-1000 truncate">
            {getSourceLabel(row)}
          </p>
        )
      });

    if (hasPropertyOwner)
      ret.push({
        key: "owner",
        name: "Owner",
        width: 240,
        minWidth: 80,
        resizable: true,
        cellClass: "truncate",
        renderCell: ({ row }) => row.owner || "--"
      });

    if (showPropertyGroup)
      ret.push({
        key: "property_group.name",
        name: "Property group",
        width: 240,
        minWidth: 100,
        resizable: true,
        renderCell: ({ row }) => (
          <p className="text-content-1000 font-medium truncate">
            {row.property_group?.name || "--"}
          </p>
        )
      });

    return ret;
  }, [type.isBoomScreen, type.isPMS]);

  return (
    <DataGridTable
      onRowClick={
        type.isBoomScreen
          ? (row) => {
              navigate(`/properties/${(row as undefined | Property)?.id}`);
            }
          : undefined
      }
      loading={isLoading}
      columns={columns}
      rows={data}
      footer={pagination}
      perPageRows={perPageRows}
      viewPortHeight={sectionHeight}
      viewPortHeightOffset={viewPortHeightOffset}
      noRowsFallback={
        <NoRowsFallback
          Icon={Search01Icon}
          title={
            hasSearchParams
              ? "No results found"
              : `${Properties} will appear here`
          }
          text={
            hasSearchParams
              ? "We couldn't find any data matching your query"
              : `Add ${propertyDictionary} to be able to invite applicants for screening`
          }
        />
      }
    />
  );
};
